import { useState } from "react";
import { useUIStore } from "../../store/uiStore";
import { drawerPalette, type DrawerPalette } from "../../lib/drawerTheme";
import type { EquityFinding, SviQuintile, SviTheme } from "../../types/equity";

// Plain-language counterpart to the technical `EquitySection` at the bottom
// of the drawer. Lives directly under RiskPanel so a non-statistician
// reader sees the equity story without scrolling past the chat.

const QUINTILE_PHRASE: Record<SviQuintile, string> = {
  Q1: "most-vulnerable 20%",
  Q2: "more-vulnerable than average",
  Q3: "average vulnerability",
  Q4: "less-vulnerable than average",
  Q5: "least-vulnerable 20%",
};

const QUINTILE_COLOR: Record<SviQuintile, string> = {
  Q1: "#dc2626",
  Q2: "#f97316",
  Q3: "#6b7280",
  Q4: "#10b981",
  Q5: "#059669",
};

const THEME_PHRASE: Record<SviTheme, string> = {
  RPL_THEME1: "socioeconomic factors (income, employment)",
  RPL_THEME2: "household composition and age",
  RPL_THEME3: "racial and ethnic minority status",
  RPL_THEME4: "housing type and transportation",
};

export function EquityImpactPanel({ equity }: { equity: EquityFinding }) {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  const gapPp = equity.delta_eo_global * 100;
  const ciLowPp = equity.delta_eo_global_ci.lower * 100;
  const ciHighPp = equity.delta_eo_global_ci.upper * 100;
  const ciExcludesZero = ciLowPp > 0 || ciHighPp < 0;
  const gapDirection = gapPp < 0
    ? "fewer real fires caught here than statewide"
    : gapPp > 0
      ? "more cautious here than statewide"
      : "matches the statewide rate";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: palette.bodyText }}>
          Community impact
        </h3>
        <EquityInfoIcon palette={palette} />
      </div>

      <div
        style={{
          padding: "8px 10px",
          background: palette.panelBg,
          border: `1px solid ${palette.panelBorder}`,
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.5,
          color: palette.bodyText,
        }}
      >
        <div style={{ marginBottom: 4 }}>
          This area is in the{" "}
          <span style={{ color: QUINTILE_COLOR[equity.svi_quintile], fontWeight: 600 }}>
            {QUINTILE_PHRASE[equity.svi_quintile]}
          </span>{" "}
          of California communities, driven mainly by{" "}
          <strong>{THEME_PHRASE[equity.dominant_theme]}</strong>.
        </div>
        <div style={{ color: palette.bodyText }}>
          Detection gap statewide:{" "}
          <strong>
            {Math.abs(gapPp).toFixed(1)} percentage-point {gapPp < 0 ? "shortfall" : "surplus"}
          </strong>{" "}
          ({gapDirection}). 95% range:{" "}
          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: palette.mutedText }}>
            {ciLowPp >= 0 ? "+" : ""}{ciLowPp.toFixed(1)} pp to {ciHighPp >= 0 ? "+" : ""}{ciHighPp.toFixed(1)} pp
          </span>
          {ciExcludesZero ? " — gap is statistically real." : " — could still be random noise."}
        </div>
      </div>
    </div>
  );
}

function EquityInfoIcon({ palette }: { palette: DrawerPalette }) {
  const [open, setOpen] = useState(false);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        aria-label="What does community impact mean?"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{
          width: 16,
          height: 16,
          padding: 0,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: "14px",
          color: palette.infoBtnText,
          background: palette.infoBtnBg,
          border: `1px solid ${palette.infoBtnBorder}`,
          borderRadius: "50%",
          cursor: "help",
        }}
      >
        i
      </button>
      {open && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "120%",
            left: 0,
            zIndex: 10,
            width: 300,
            padding: "8px 10px",
            background: palette.tooltipBg,
            color: palette.tooltipText,
            border: `1px solid ${palette.tooltipBorder}`,
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.5,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>What this panel shows</div>
          <p style={{ margin: "0 0 6px" }}>
            <span style={{ color: "#dc2626", fontWeight: 600 }}>Community type</span> —
            from CDC/ATSDR Social Vulnerability Index (SVI). Census tracts are
            ranked into 5 groups of 20% each; this row tells you which group
            this neighborhood falls into and what's driving its score.
          </p>
          <p style={{ margin: "0 0 6px" }}>
            <span style={{ color: palette.accentLink, fontWeight: 600 }}>Detection gap</span> —
            how the model's accuracy across communities of this type compares
            to the statewide average. A <em>shortfall</em> means the model
            historically misses more real fires here than in the average
            community; a <em>surplus</em> means it triggers extra alerts here.
            Stated in percentage points (e.g. 3.8 pp = 3.8 fewer per 100).
          </p>
          <p style={{ margin: 0, color: palette.tooltipMutedText }}>
            <b>95% range</b> — if it crosses zero, the gap could be random
            noise. If it stays on one side of zero, the gap is statistically
            real and worth taking seriously.
          </p>
        </div>
      )}
    </span>
  );
}
